import { useEffect, useMemo, useRef, useState } from "react";
import { Spin, Alert } from "antd";
import { db1995API } from "../services/api";
import "./ArchivoTexto.css";

type SectionMeta = { key: string; title: string };

type CatalogItem = {
  id: number;
  file_name: string;
  case_id?: number;
  file_type?: string;
  plain_text_chars?: number;
  preview?: string;
  public_date?: string | null;
  pdf_url: string;
  text_url: string;
  db1995_text_url?: string;
};

type SearchItem = {
  id: number;
  file_name: string;
  reference_title?: string;
  index_name?: string;
  public_date?: string | null;
  snippet?: string;
  highlighted_snippet?: string;
  pdf_url?: string;
  db1995_text_url?: string;
};

const FALLBACK_SECTIONS: SectionMeta[] = [
  { key: "PENAL", title: "COMARCA PENAL (64-08)" },
  { key: "NO", title: "COMARCA NOTARIAL / ALODIAL" },
  { key: "VARIOS", title: "COMARCA VARIOS (REGISTRO NACIONAL)" },
  { key: "FECC", title: "FECC / FELCC DENUNCIA WARNES" },
  { key: "FONDESIF", title: "FONDESIF EL VALLECITO" },
  { key: "CASO", title: "PIEZAS DEL CASO / OTROS" },
];

const API_BASE_URL = process.env.REACT_APP_API_URL || "https://comarca.5bc.us/api";
const ABSOLUTE_ORIGIN = API_BASE_URL.replace(/\/api\/?$/, "");

function asAbsolute(url: string) {
  if (!url) return "#";
  if (url.startsWith("http://") || url.startsWith("https://")) return url;
  return `${ABSOLUTE_ORIGIN}${url}`;
}

function renderHighlighted(text?: string) {
  const value = text || "";
  if (!value) return "Sin fragmento";
  const parts = value.split(/\[\[H\]\]|\[\[\/H\]\]/);
  return parts.map((part, index) =>
    index % 2 === 1 ? (
      <mark key={`h-${index}`}>{part}</mark>
    ) : (
      <span key={`t-${index}`}>{part}</span>
    )
  );
}

export default function ArchivoTexto() {
  const [sections, setSections] = useState<Record<string, number>>({});
  const [sectionMeta, setSectionMeta] = useState<SectionMeta[]>(FALLBACK_SECTIONS);
  const [activeSection, setActiveSection] = useState("PENAL");
  const [items, setItems] = useState<CatalogItem[]>([]);
  const [sectionTotal, setSectionTotal] = useState(0);
  const [loadingCatalog, setLoadingCatalog] = useState(true);
  const [loadingSection, setLoadingSection] = useState(false);
  const [error, setError] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchResults, setSearchResults] = useState<SearchItem[]>([]);
  const [readerOpen, setReaderOpen] = useState(false);
  const [readerTitle, setReaderTitle] = useState("");
  const [readerText, setReaderText] = useState("");
  const [readerLoading, setReaderLoading] = useState(false);
  const [savingDateId, setSavingDateId] = useState<number | null>(null);
  const [savedFlashId, setSavedFlashId] = useState<number | null>(null);
  const savedDatesRef = useRef<Record<number, string>>({});

  const totalFiles = useMemo(
    () => Object.values(sections).reduce((sum, n) => sum + (n || 0), 0),
    [sections]
  );

  const savePublicDate = async (id: number, value: string) => {
    const next = value.trim();
    if (next) {
      const year = Number(next.slice(0, 4));
      if (!/^\d{4}-\d{2}-\d{2}$/.test(next) || year < 1900 || year > 2100) {
        setError(
          "La fecha pública debe estar entre 1900 y 2100 (formato AAAA-MM-DD). Use Guardar fecha después de elegir el año completo."
        );
        return;
      }
    }
    const previous = savedDatesRef.current[id] || "";
    if (next === previous) {
      setSavedFlashId(id);
      window.setTimeout(() => {
        setSavedFlashId((curr) => (curr === id ? null : curr));
      }, 1200);
      return;
    }
    setSavingDateId(id);
    setError("");
    try {
      const public_date = next || null;
      await db1995API.update(id, { public_date });
      savedDatesRef.current[id] = next;
      setItems((prev) =>
        prev.map((item) =>
          item.id === id ? { ...item, public_date } : item
        )
      );
      setSearchResults((prev) =>
        prev.map((item) =>
          item.id === id ? { ...item, public_date } : item
        )
      );
      setSavedFlashId(id);
      window.setTimeout(() => {
        setSavedFlashId((curr) => (curr === id ? null : curr));
      }, 1800);
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.message || "No se pudo guardar la fecha");
    } finally {
      setSavingDateId(null);
    }
  };

  useEffect(() => {
    const load = async () => {
      setLoadingCatalog(true);
      setError("");
      try {
        const data = await db1995API.getCatalog();
        setSections(data?.sections || {});
        if (Array.isArray(data?.section_meta) && data.section_meta.length) {
          setSectionMeta(data.section_meta);
        }
        const firstWithFiles =
          (data?.section_meta || FALLBACK_SECTIONS).find(
            (s: SectionMeta) => (data?.sections?.[s.key] || 0) > 0
          )?.key || "PENAL";
        setActiveSection(firstWithFiles);
      } catch (e: any) {
        setError(e?.response?.data?.detail || e?.message || "No se pudo cargar el archivo");
      } finally {
        setLoadingCatalog(false);
      }
    };
    load();
  }, []);

  useEffect(() => {
    if (!activeSection) return;
    const loadSection = async () => {
      setLoadingSection(true);
      setError("");
      try {
        const response = await db1995API.getCatalogSection(activeSection, 0, 200);
        const nextItems = response?.items || [];
        setItems(nextItems);
        setSectionTotal(response?.total || 0);
        const map: Record<number, string> = { ...savedDatesRef.current };
        nextItems.forEach((item: CatalogItem) => {
          map[item.id] = item.public_date
            ? String(item.public_date).slice(0, 10)
            : "";
        });
        savedDatesRef.current = map;
      } catch (e: any) {
        setError(e?.response?.data?.detail || e?.message || "No se pudo abrir la sección");
        setItems([]);
        setSectionTotal(0);
      } finally {
        setLoadingSection(false);
      }
    };
    loadSection();
  }, [activeSection]);

  const activeTitle =
    sectionMeta.find((s) => s.key === activeSection)?.title || activeSection;

  const openReader = async (id: number, title: string) => {
    setReaderTitle(title);
    setReaderOpen(true);
    setReaderLoading(true);
    setReaderText("");
    try {
      const text = await db1995API.getPlainText(id);
      setReaderText(text || "Sin texto guardado para este archivo.");
    } catch {
      setReaderText("Error al cargar el texto.");
    } finally {
      setReaderLoading(false);
    }
  };

  const runSearch = async (value?: string) => {
    const q = (value ?? searchTerm).trim();
    if (!q) {
      setSearchResults([]);
      return;
    }
    setSearchLoading(true);
    setError("");
    try {
      const response = await db1995API.search(q, 1000);
      setSearchResults(response?.items || []);
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.message || "No se pudo buscar");
    } finally {
      setSearchLoading(false);
    }
  };

  return (
    <div className="archivo-page">
      <header className="archivo-hero">
        <h1 className="archivo-brand">Comarca</h1>
        <p className="archivo-headline">Archivo de texto del expediente</p>
        <p className="archivo-lede">
          Caso 7011992000740314 — Asociación Delictuosa, Santa Cruz. Lectura del
          corpus OCR y documentos cargados ({totalFiles || "…"} archivos).
        </p>
        <div className="archivo-cta-row">
          <button
            type="button"
            className="archivo-btn archivo-btn-primary"
            onClick={() => {
              document.getElementById("archivo-buscar")?.scrollIntoView({
                behavior: "smooth",
                block: "start",
              });
            }}
          >
            Buscar en el texto
          </button>
          <button
            type="button"
            className="archivo-btn archivo-btn-ghost"
            onClick={() => {
              document.getElementById("archivo-secciones")?.scrollIntoView({
                behavior: "smooth",
                block: "start",
              });
            }}
          >
            Explorar secciones
          </button>
        </div>
      </header>

      <div className="archivo-shell">
        {error ? <Alert type="error" showIcon message={error} /> : null}

        <section className="archivo-search" id="archivo-buscar">
          <label htmlFor="archivo-q">Buscar en nombre + texto OCR</label>
          <div className="archivo-search-row">
            <input
              id="archivo-q"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") runSearch();
              }}
              placeholder='Ej: "Camacho" + "apelación"  ·  Use + para AND'
            />
            <button
              type="button"
              className="archivo-btn archivo-btn-primary"
              onClick={() => runSearch()}
              disabled={searchLoading}
            >
              {searchLoading ? "Buscando…" : "Buscar"}
            </button>
          </div>
          {searchResults.length > 0 ? (
            <div className="archivo-panel" style={{ marginTop: 8 }}>
              <h2>Resultados</h2>
              <p className="meta">{searchResults.length} coincidencias</p>
              {searchResults.map((item) => (
                <article className="archivo-doc archivo-hit" key={`s-${item.id}`}>
                  <h3>{item.file_name}</h3>
                  <p className="preview">
                    {renderHighlighted(item.highlighted_snippet || item.snippet)}
                  </p>
                  <div className="archivo-doc-actions">
                    <button
                      type="button"
                      className="archivo-linkish"
                      onClick={() => openReader(item.id, item.file_name)}
                    >
                      Leer texto
                    </button>
                    {item.pdf_url ? (
                      <a
                        className="archivo-linkish"
                        href={asAbsolute(item.pdf_url)}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Descargar
                      </a>
                    ) : null}
                  </div>
                </article>
              ))}
            </div>
          ) : null}
        </section>

        <div className="archivo-layout" id="archivo-secciones">
          <nav className="archivo-sections" aria-label="Secciones del archivo">
            {loadingCatalog ? <Spin /> : null}
            {sectionMeta.map((section) => {
              const count = sections[section.key] || 0;
              return (
                <button
                  key={section.key}
                  type="button"
                  className={`archivo-section-btn${
                    activeSection === section.key ? " active" : ""
                  }`}
                  onClick={() => setActiveSection(section.key)}
                >
                  <strong>{section.key}</strong>
                  <span>{section.title}</span>
                  <em>{count} archivos</em>
                </button>
              );
            })}
          </nav>

          <main className="archivo-main">
            <section className="archivo-panel">
              <h2>{activeTitle}</h2>
              <p className="meta">
                {sectionTotal} archivos en esta sección · ordenados por fecha
                pública (ascendente) · texto de PDF, Word e imágenes
              </p>
              {loadingSection ? <Spin /> : null}
              {!loadingSection && items.length === 0 ? (
                <p className="archivo-empty">Sin archivos en esta sección.</p>
              ) : null}
              {!loadingSection
                ? items.map((item) => (
                    <article className="archivo-doc" key={item.id}>
                      <h3>
                        <span className="archivo-chip">
                          {item.file_type || "archivo"}
                        </span>
                        {item.file_name}
                      </h3>
                      <p className="preview">
                        {item.preview ||
                          (item.plain_text_chars
                            ? `${item.plain_text_chars.toLocaleString()} caracteres guardados`
                            : "Sin vista previa de texto")}
                      </p>
                      <div className="archivo-date-row">
                        <label htmlFor={`public-date-${item.id}`}>
                          Fecha pública
                        </label>
                        <input
                          id={`public-date-${item.id}`}
                          type="date"
                          min="1900-01-01"
                          max="2100-12-31"
                          value={item.public_date ? String(item.public_date).slice(0, 10) : ""}
                          disabled={savingDateId === item.id}
                          onChange={(e) => {
                            const next = e.target.value;
                            // Only update local draft while picking; save with the button
                            // so Safari year navigation (e.g. 0002) is not written to the DB.
                            setItems((prev) =>
                              prev.map((row) =>
                                row.id === item.id
                                  ? { ...row, public_date: next || null }
                                  : row
                              )
                            );
                          }}
                        />
                        <button
                          type="button"
                          className="archivo-linkish"
                          disabled={savingDateId === item.id}
                          onClick={() =>
                            void savePublicDate(
                              item.id,
                              item.public_date
                                ? String(item.public_date).slice(0, 10)
                                : ""
                            )
                          }
                        >
                          Guardar fecha
                        </button>
                        {savingDateId === item.id ? (
                          <span className="archivo-date-saving">Guardando…</span>
                        ) : null}
                        {savedFlashId === item.id ? (
                          <span className="archivo-date-saved">Guardado</span>
                        ) : null}
                      </div>
                      <div className="archivo-doc-actions">
                        <button
                          type="button"
                          className="archivo-linkish"
                          onClick={() => openReader(item.id, item.file_name)}
                        >
                          Leer texto completo
                        </button>
                        <a
                          className="archivo-linkish"
                          href={asAbsolute(item.pdf_url)}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          Descargar original
                        </a>
                        {item.db1995_text_url ? (
                          <a
                            className="archivo-linkish"
                            href={asAbsolute(item.db1995_text_url)}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            Abrir texto
                          </a>
                        ) : null}
                      </div>
                    </article>
                  ))
                : null}
            </section>
          </main>
        </div>
      </div>

      {readerOpen ? (
        <div
          className="archivo-reader-backdrop"
          role="dialog"
          aria-modal="true"
          onClick={() => setReaderOpen(false)}
        >
          <div
            className="archivo-reader"
            onClick={(e) => e.stopPropagation()}
          >
            <header>
              <div>
                <h2>{readerTitle}</h2>
                <p className="meta" style={{ margin: 0 }}>
                  Texto guardado en db1995
                </p>
              </div>
              <button
                type="button"
                className="archivo-linkish"
                onClick={() => setReaderOpen(false)}
              >
                Cerrar
              </button>
            </header>
            {readerLoading ? <Spin /> : <pre>{readerText}</pre>}
          </div>
        </div>
      ) : null}
    </div>
  );
}
